import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Avatar,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  Event as EventIcon,
  Store as StoreIcon,
  Assignment as AssignmentIcon,
  Slideshow as PresentationIcon,
  Contacts as ContactsIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { DashboardStats } from '../types';
import apiService from '../services/api';

interface StatCardProps {
  title: string;
  value: number;
  change?: number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  icon,
  color,
}) => (
  <Card
    sx={{
      height: '100%',
      boxShadow:
        '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      border: '1px solid #e5e7eb',
      borderRadius: 2,
      '&:hover': {
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#6b7280',
              mb: 0.5,
              textTransform: 'none',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.875rem',
              fontWeight: 700,
              color: '#111827',
              lineHeight: 1.2,
            }}
          >
            {value.toLocaleString()}
          </Typography>
          {change && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              {change > 0 ? (
                <TrendingUpIcon
                  sx={{ fontSize: 16, color: '#10b981', mr: 0.5 }}
                />
              ) : (
                <TrendingDownIcon
                  sx={{ fontSize: 16, color: '#ef4444', mr: 0.5 }}
                />
              )}
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: change > 0 ? '#10b981' : '#ef4444',
                  fontWeight: 500,
                }}
              >
                {change > 0 ? '+' : ''}
                {change}% from last month
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ActivityItem: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color: string;
}> = ({ icon, title, subtitle, color }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      p: 1.5,
      bgcolor: '#f9fafb',
      borderRadius: 2,
      mb: 1,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        mr: 1.5,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const data = await apiService.getDashboardStats();
        setStats(data);
      } catch (err: any) {
        setError(
          err.response?.data?.message || 'Failed to load dashboard data'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return null;
  }

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const userTypeData = stats.usersByType.map((item, index) => ({
    name: item.type,
    value: parseInt(item.count),
    color: COLORS[index % COLORS.length],
  }));

  // Mock growth data for charts
  const growthData = [
    { name: 'Jan', users: 400, revenue: 24000 },
    { name: 'Feb', users: 600, revenue: 35000 },
    { name: 'Mar', users: 800, revenue: 45000 },
    { name: 'Apr', users: 1000, revenue: 52000 },
    { name: 'May', users: 1200, revenue: 61000 },
    { name: 'Jun', users: stats.totalUsers, revenue: 65000 },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.875rem',
            fontWeight: 700,
            color: '#111827',
          }}
        >
          Dashboard Overview
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          sx={{
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 1,
          }}
        >
          Export Report
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            change={12.5}
            icon={<PeopleIcon />}
            color="#3b82f6"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Exhibitors"
            value={stats.totalExhibitors}
            change={8.2}
            icon={<StoreIcon />}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Sessions"
            value={stats.totalSessions}
            change={5.1}
            icon={<PresentationIcon />}
            color="#8b5cf6"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Total Leads"
            value={stats.totalLeads}
            change={15.3}
            icon={<ContactsIcon />}
            color="#f59e0b"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Card
            sx={{
              boxShadow:
                '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              border: '1px solid #e5e7eb',
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#111827',
                  mb: 2,
                }}
              >
                User Growth
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              boxShadow:
                '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              border: '1px solid #e5e7eb',
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#111827',
                  mb: 2,
                }}
              >
                User Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={userTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {userTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 2,
                }}
              >
                {userTypeData.map((entry, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: entry.color,
                      }}
                    />
                    <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {entry.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Card
        sx={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            sx={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#111827',
              mb: 2,
            }}
          >
            Recent Activity
          </Typography>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            <ActivityItem
              icon={<PeopleIcon sx={{ fontSize: 16 }} />}
              title="New exhibitor registered"
              subtitle="Tech Innovations Inc. - 2 minutes ago"
              color="#10b981"
            />
            <ActivityItem
              icon={<EventIcon sx={{ fontSize: 16 }} />}
              title="Session scheduled"
              subtitle="AI Workshop - Hall A - 5 minutes ago"
              color="#3b82f6"
            />
            <ActivityItem
              icon={<PresentationIcon sx={{ fontSize: 16 }} />}
              title="Speaker confirmed"
              subtitle="Dr. Sarah Johnson - Blockchain Summit - 10 minutes ago"
              color="#8b5cf6"
            />
            {stats.recentRegistrations
              .slice(0, 3)
              .map((registration, index) => (
                <ActivityItem
                  key={index}
                  icon={<AssignmentIcon sx={{ fontSize: 16 }} />}
                  title="New registration"
                  subtitle={`${registration.user?.firstName} ${registration.user?.lastName} - ${registration.event?.eventName}`}
                  color="#f59e0b"
                />
              ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
